import React, { useState } from 'react';
import { QRType, QRCategory } from '@/types/qr';
import {
  Globe,
  Type,
  Mail,
  Phone,
  MessageSquare,
  Wifi,
  Contact,
  MapPin,
  Calendar,
  CreditCard,
  Share2,
  Smartphone,
  Coins,
  Code2,
  Search,
  LayoutGrid,
  X
} from 'lucide-react';

interface ContentTypeSelectorProps {
  selectedType: QRType;
  onSelectType: (type: QRType) => void;
}

interface TypeItem {
  type: QRType;
  category: QRCategory;
  name: string;
  description: string;
  icon: React.ReactNode;
}

export const CONTENT_TYPES: TypeItem[] = [
  // Personal
  {
    type: 'url',
    category: 'personal',
    name: 'Website URL',
    description: 'Link to any website or page',
    icon: <Globe className="h-5 w-5 text-primary" />
  },
  {
    type: 'wifi',
    category: 'business',
    name: 'Wi-Fi Network',
    description: 'Connect guests without typing passwords',
    icon: <Wifi className="h-5 w-5 text-primary" />
  },
  {
    type: 'vcard',
    category: 'personal',
    name: 'Contact vCard',
    description: 'Save contact details to phone',
    icon: <Contact className="h-5 w-5 text-primary" />
  },
  {
    type: 'upi',
    category: 'business',
    name: 'UPI Payment',
    description: 'Instant pay for India NPCI UPI',
    icon: <CreditCard className="h-5 w-5 text-primary" />
  },
  {
    type: 'text',
    category: 'personal',
    name: 'Plain Text',
    description: 'Freeform notes or messages',
    icon: <Type className="h-5 w-5 text-muted-foreground" />
  },
  {
    type: 'email',
    category: 'personal',
    name: 'Email',
    description: 'Pre-filled mailto message',
    icon: <Mail className="h-5 w-5 text-primary" />
  },
  {
    type: 'phone',
    category: 'personal',
    name: 'Phone Call',
    description: 'Direct phone dialer',
    icon: <Phone className="h-5 w-5 text-primary" />
  },
  {
    type: 'sms',
    category: 'personal',
    name: 'SMS Message',
    description: 'Pre-written text message',
    icon: <MessageSquare className="h-5 w-5 text-primary" />
  },
  {
    type: 'event',
    category: 'business',
    name: 'Calendar Event',
    description: 'Add event to Google/Apple calendar',
    icon: <Calendar className="h-5 w-5 text-primary" />
  },
  {
    type: 'geo',
    category: 'business',
    name: 'Location / Maps',
    description: 'GPS coordinates & directions',
    icon: <MapPin className="h-5 w-5 text-primary" />
  },
  {
    type: 'social',
    category: 'business',
    name: 'Social Media',
    description: 'Instagram, X, YouTube, LinkedIn, GitHub',
    icon: <Share2 className="h-5 w-5 text-primary" />
  },
  {
    type: 'app',
    category: 'other',
    name: 'App Download',
    description: 'iOS App Store & Android Play Store',
    icon: <Smartphone className="h-5 w-5 text-primary" />
  },
  {
    type: 'crypto',
    category: 'other',
    name: 'Cryptocurrency',
    description: 'BTC, ETH, Solana wallet payments',
    icon: <Coins className="h-5 w-5 text-primary" />
  },
  {
    type: 'custom',
    category: 'other',
    name: 'Custom Protocol',
    description: 'Raw URI or specialized payload',
    icon: <Code2 className="h-5 w-5 text-muted-foreground" />
  }
];

const POPULAR_TYPES: QRType[] = ['url', 'wifi', 'vcard', 'text', 'email'];

export const ContentTypeSelector: React.FC<ContentTypeSelectorProps> = ({
  selectedType,
  onSelectType
}) => {
  const [activeCategory, setActiveCategory] = useState<QRCategory | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isGridModalOpen, setIsGridModalOpen] = useState(false);
  const [showAllOnDesktop, setShowAllOnDesktop] = useState(false);

  const filtered = CONTENT_TYPES.filter((item) => {
    const matchesCat = activeCategory === 'all' || item.category === activeCategory;
    const matchesQuery =
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCat && matchesQuery;
  });

  // Default desktop view shows only the popular formats until expanded or filtered
  const isDefaultView = activeCategory === 'all' && !searchQuery && !showAllOnDesktop;
  const desktopItems = isDefaultView
    ? filtered.filter((item) => POPULAR_TYPES.includes(item.type))
    : filtered;

  const selectedItem = CONTENT_TYPES.find((i) => i.type === selectedType) || CONTENT_TYPES[0];

  return (
    <div className="space-y-3">
      {/* ----------------- MOBILE VIEW: Compact Swipeable Horizontal Chips & Modal Trigger ----------------- */}
      <div className="md:hidden space-y-2.5">
        <div className="flex items-center justify-between">
          <span className="text-[12px] font-semibold text-muted-foreground uppercase tracking-wider">
            Format: <strong className="text-foreground capitalize">{selectedItem.name}</strong>
          </span>
          <button
            onClick={() => setIsGridModalOpen(true)}
            className="flex items-center gap-1 text-[12px] text-primary font-medium cursor-pointer"
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            <span>All Formats</span>
          </button>
        </div>

        {/* Horizontal Swipe Carousel */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar -mx-2 px-2">
          {CONTENT_TYPES.slice(0, 8).map((item) => {
            const isSelected = selectedType === item.type;
            return (
              <button
                key={item.type}
                onClick={() => onSelectType(item.type)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-full border text-[13px] font-medium whitespace-nowrap transition-all shrink-0 cursor-pointer active:scale-95 shadow-xs ${
                  isSelected
                    ? 'bg-primary border-primary text-primary-foreground'
                    : 'bg-card border-border text-foreground'
                }`}
              >
                <div className={`shrink-0 ${isSelected ? 'text-primary-foreground' : ''}`}>
                  {React.cloneElement(item.icon as React.ReactElement<any>, { className: 'h-4 w-4' })}
                </div>
                <span>{item.name}</span>
              </button>
            );
          })}

          <button
            onClick={() => setIsGridModalOpen(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-dashed border-border text-muted-foreground text-[13px] font-medium whitespace-nowrap shrink-0 cursor-pointer"
          >
            <span>+ More</span>
          </button>
        </div>
      </div>

      {/* ----------------- DESKTOP VIEW: Full Store Utility Grid ----------------- */}
      <div className="hidden md:block space-y-4">
        {/* Category Pills & Search */}
        <div className="flex items-center justify-between gap-2.5">
          <div className="flex items-center gap-1.5 p-1 bg-muted rounded-full overflow-x-auto">
            {(['all', 'personal', 'business', 'other'] as const).map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-3.5 py-1.5 rounded-full text-[12px] font-normal tracking-tight transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-card text-primary font-semibold shadow-xs'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {cat === 'all' ? 'All Types' : cat}
              </button>
            ))}
          </div>

          <div className="relative flex-1 max-w-xs">
            <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search formats..."
              className="w-full pl-9 pr-3.5 py-1.5 text-[13px] rounded-full bg-card border border-border text-foreground placeholder-muted-foreground focus:outline-none focus:border-ring shadow-xs tracking-tight"
            />
          </div>
        </div>

        {isDefaultView && (
          <div className="flex items-center justify-between">
            <span className="text-[12px] font-semibold text-muted-foreground uppercase tracking-wider">
              Popular Formats
            </span>
            <button
              onClick={() => setShowAllOnDesktop(true)}
              className="text-[12px] text-primary font-medium cursor-pointer"
            >
              All formats ({CONTENT_TYPES.length})
            </button>
          </div>
        )}

        {/* Grid of Store Utility Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {desktopItems.map((item) => {
            const isSelected = selectedType === item.type;
            return (
              <button
                key={item.type}
                onClick={() => onSelectType(item.type)}
                className={`flex flex-col text-left p-3.5 rounded-2xl border transition-all duration-150 cursor-pointer relative overflow-hidden active:scale-95 shadow-xs ${
                  isSelected
                    ? 'bg-primary/5 border-primary ring-1 ring-primary'
                    : 'bg-card border-border hover:border-muted-foreground'
                }`}
              >
                <div className="flex items-center justify-between w-full mb-2">
                  <div
                    className={`p-2 rounded-lg ${
                      isSelected ? 'bg-primary/10' : 'bg-muted'
                    }`}
                  >
                    {item.icon}
                  </div>
                  {isSelected && (
                    <span className="h-2 w-2 rounded-full bg-primary" />
                  )}
                </div>
                <h4 className="text-[14px] font-semibold text-foreground tracking-tight">{item.name}</h4>
                <p className="text-[12px] text-muted-foreground mt-0.5 line-clamp-1 leading-normal">
                  {item.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* ----------------- MOBILE ALL FORMATS MODAL SHEET ----------------- */}
      {isGridModalOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex flex-col justify-end">
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-xs transition-opacity"
            onClick={() => setIsGridModalOpen(false)}
          />
          <div className="relative z-10 w-full max-h-[80vh] overflow-y-auto bg-card rounded-t-3xl border-t border-border p-5 pb-[max(2rem,env(safe-area-inset-bottom))] shadow-2xl space-y-4 animate-in slide-in-from-bottom duration-200">
            <div className="flex items-center justify-between pb-2 border-b border-border">
              <h3 className="text-[16px] font-semibold text-foreground tracking-tight">
                All 14 QR Formats
              </h3>
              <button
                onClick={() => setIsGridModalOpen(false)}
                className="p-1 text-muted-foreground hover:text-foreground rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              {CONTENT_TYPES.map((item) => {
                const isSelected = selectedType === item.type;
                return (
                  <button
                    key={item.type}
                    onClick={() => {
                      onSelectType(item.type);
                      setIsGridModalOpen(false);
                    }}
                    className={`flex items-start gap-2.5 p-3 rounded-xl border text-left transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-primary/10 border-primary'
                        : 'bg-muted border-border'
                    }`}
                  >
                    <div className="p-1.5 rounded-lg bg-card shadow-xs">
                      {item.icon}
                    </div>
                    <div>
                      <h5 className="text-[13px] font-semibold text-foreground leading-tight">
                        {item.name}
                      </h5>
                      <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{item.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
